// om-rotated.ts — rotated lat/lon (rotated-pole) grid support.
//
// Some Open-Meteo models store a grid that is REGULAR in a ROTATED spherical
// coordinate system, not in true lat/lon (e.g. GEM HRDPS: crs_wkt is a GEOGCRS
// "Rotated Lat/Lon" with a PROJ `ob_tran` deriving conversion). Rendering such a
// grid through the plain-geographic path treats rotated coords as true lat/lon,
// which shears the field badly (most visibly far from the rotated pole).
//
// The grid's array axes are rotated-longitude (col) and rotated-latitude (row),
// with the rotated NORTH pole sitting at true geographic (lonP, latP). We convert
// each grid point's rotated coords back to true lat/lon via a rigid sphere
// rotation (rotation MATRIX, not the proj4 `ob_tran` chain — proj4js's ob_tran
// forward/inverse units are ambiguous and were not self-consistent here).
//
// VERIFIED against GEM HRDPS (rotated N pole 36.08852°N, 65.305142°E, 0.0225°
// grid, 2540×1290): first grid point (39.626°N, −133.630°E) and last grid point
// round-trip to the published SW/NE corners exactly, and toRot→toGeo is identity.

const D2R = Math.PI / 180
const R2D = 180 / Math.PI

export interface RotatedPoleParams {
  /** True geographic longitude of the rotated grid's NORTH pole (degrees). */
  lonP: number
  /** True geographic latitude of the rotated grid's NORTH pole (degrees). */
  latP: number
  /** Central rotated longitude (PROJ o_lon_p / CF north_pole_grid_longitude). */
  cRotLon: number
}

export interface RotatedTransform {
  /** rotated (rlon,rlat in DEGREES) → true [lon,lat] in degrees. */
  toGeo(rlonDeg: number, rlatDeg: number): [number, number]
  /** true (lon,lat in DEGREES) → rotated [rlon,rlat] in degrees. */
  toRot(lonDeg: number, latDeg: number): [number, number]
}

// Pull PARAMETER["<name>",<value>] from a WKT string (case-insensitive).
function wktParam(wkt: string, name: string): number | null {
  const re = new RegExp(`PARAMETER\\[\\s*"${name}"\\s*,\\s*(-?[\\d.]+)`, 'i')
  const m = wkt.match(re)
  return m ? Number(m[1]) : null
}

// Parse rotated-pole params from a crs_wkt, or null if it isn't a rotated grid.
// PROJ `ob_tran` encodes: lon_0 = poleLon + 180, o_lat_p = poleLat, o_lon_p =
// central rotated longitude.
export function parseRotatedWkt(crsWkt: string): RotatedPoleParams | null {
  if (!crsWkt.includes('ob_tran') && !crsWkt.includes('Rotated Lat/Lon')) return null
  const lon0 = wktParam(crsWkt, 'lon_0')
  const latP = wktParam(crsWkt, 'o_lat_p')
  const oLonP = wktParam(crsWkt, 'o_lon_p') ?? 0
  if (lon0 == null || latP == null) return null
  return { lonP: lon0 - 180, latP, cRotLon: oLonP }
}

// Build the rotated↔true transform pair via a rigid rotation that sends the
// rotated north pole (lonP,latP) to the geographic north pole:
//   R = Ry(latP − 90°) · Rz(−lonP)   (geo → rotated);  Rᵀ = rotated → geo.
// `cRotLon` rotates the rotated frame about its own pole (offsets rotated lon).
export function makeRotatedTransform(p: RotatedPoleParams): RotatedTransform {
  const a = p.latP * D2R - Math.PI / 2
  const ca = Math.cos(a)
  const sa = Math.sin(a)
  const cl = Math.cos(p.lonP * D2R)
  const sl = Math.sin(p.lonP * D2R)
  // R (geo → rotated), row-major.
  const R = [
    [ca * cl, ca * sl, sa],
    [-sl, cl, 0],
    [-sa * cl, -sa * sl, ca],
  ]
  const clamp = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v)
  const wrap180 = (d: number): number => ((((d + 180) % 360) + 360) % 360) - 180

  return {
    toRot(lonDeg, latDeg) {
      const lo = lonDeg * D2R
      const la = latDeg * D2R
      const v = [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)]
      const x = R[0][0] * v[0] + R[0][1] * v[1] + R[0][2] * v[2]
      const y = R[1][0] * v[0] + R[1][1] * v[1] + R[1][2] * v[2]
      const z = R[2][0] * v[0] + R[2][1] * v[1] + R[2][2] * v[2]
      return [wrap180(Math.atan2(y, x) * R2D - p.cRotLon), Math.asin(clamp(z)) * R2D]
    },
    toGeo(rlonDeg, rlatDeg) {
      const lo = (rlonDeg + p.cRotLon) * D2R
      const la = rlatDeg * D2R
      const v = [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)]
      // Rᵀ · v (transpose of R).
      const x = R[0][0] * v[0] + R[1][0] * v[1] + R[2][0] * v[2]
      const y = R[0][1] * v[0] + R[1][1] * v[1] + R[2][1] * v[2]
      const z = R[0][2] * v[0] + R[1][2] * v[1] + R[2][2] * v[2]
      return [wrap180(Math.atan2(y, x) * R2D), Math.asin(clamp(z)) * R2D]
    },
  }
}
